import { useState, useEffect } from 'react';
import { useAuthStore } from '../features/auth/stores/useAuthStore';
import { api } from '../lib/api';

const DISMISSAL_KEY = 'promo_popup_dismissed_at';
const SIDE_TAB_DISMISSAL_KEY = 'promo_side_tab_dismissed';

export const usePromoPopup = () => {
    const isAuthenticated = useAuthStore(state => state.isAuthenticated);
    const [isOpen, setIsOpen] = useState(false);
    
    // Side tab state
    const [isSideTabOpen, setIsSideTabOpen] = useState(false);
    const [hasWelcomeCoupon, setHasWelcomeCoupon] = useState(false);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        const initPromo = async () => {
            try {
                const res = await api.get('/coupons/promo-settings/public');
                if (res.data?.success && res.data?.data) {
                    const { popupEnabled, popupDelaySeconds, popupDismissalDays, welcomeCouponEnabled } = res.data.data;
                    
                    // Welcome Coupon Tab Logic
                    if (welcomeCouponEnabled) {
                        setHasWelcomeCoupon(true);
                        // Check if dismissed in this session
                        if (!sessionStorage.getItem(SIDE_TAB_DISMISSAL_KEY) && !isAuthenticated) {
                            setIsSideTabOpen(true);
                        }
                    }

                    // Popup Logic (Guest only)
                    if (popupEnabled && !isAuthenticated) {
                        const dismissedAt = localStorage.getItem(DISMISSAL_KEY);
                        if (dismissedAt) {
                            const dismissedTime = parseInt(dismissedAt, 10);
                            const now = Date.now();
                            const daysPassed = (now - dismissedTime) / (1000 * 60 * 60 * 24);
                            
                            // If they dismissed it recently, do not show
                            if (daysPassed < popupDismissalDays) {
                                return;
                            }
                        }

                        // Schedule the popup
                        timeoutId = setTimeout(() => {
                            setIsOpen(true);
                        }, popupDelaySeconds * 1000);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch promo settings', error);
            }
        };

        initPromo();

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isAuthenticated]);

    const handleClose = () => {
        setIsOpen(false);
        localStorage.setItem(DISMISSAL_KEY, Date.now().toString());
    };

    const handleSideTabClose = () => {
        setIsSideTabOpen(false);
        sessionStorage.setItem(SIDE_TAB_DISMISSAL_KEY, 'true');
    };

    return {
        isOpen,
        handleClose,
        isSideTabOpen,
        hasWelcomeCoupon,
        handleSideTabClose
    };
};
